import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatRupiah, formatDate } from "@/lib/format";
import { Contact, Plus, Pencil, Loader2, MessageCircle, Download, Upload, FileSpreadsheet } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PayStateBadge, PAY_STATUS_LABEL } from "@/components/SalesShared";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", address: "", note: "" };
const waLink = (p) => { const d = (p || "").replace(/\D/g, ""); return d ? `https://wa.me/${d.startsWith("0") ? `62${d.slice(1)}` : d}` : null; };

const saveBlob = async (path, name) => {
  try {
    const r = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Gagal mengunduh file"); }
};

function ImportButton({ onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/customers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(r.data); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const issues = res ? [...res.failed.map((x) => ({ ...x, t: "Gagal" })), ...res.skipped.map((x) => ({ ...x, t: "Dilewati" }))].sort((a, b) => a.row - b.row) : [];
  return (
    <>
      <input ref={ref} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="customer-import-input" />
      <Button variant="outline" disabled={busy} onClick={() => ref.current?.click()} className="border-brand text-brand" data-testid="customer-import-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Import
      </Button>
      <Dialog open={!!res} onOpenChange={(o) => !o && setRes(null)}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="customer-import-result">
          <DialogHeader><DialogTitle>Hasil Import Customer</DialogTitle></DialogHeader>
          {res && (
            <>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2"><p className="text-xs text-emerald-800">Berhasil</p><p className="font-bold font-mono" data-testid="import-created">{res.created}</p></div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-2"><p className="text-xs text-amber-800">Dilewati</p><p className="font-bold font-mono" data-testid="import-skipped">{res.skipped.length}</p></div>
                <div className="rounded-lg bg-rose-50 border border-rose-200 p-2"><p className="text-xs text-rose-800">Gagal</p><p className="font-bold font-mono" data-testid="import-failed">{res.failed.length}</p></div>
              </div>
              {issues.length > 0 && (
                <table className="w-full text-xs border rounded-md" data-testid="import-issues">
                  <thead className="bg-secondary text-muted-foreground"><tr><th className="text-left px-2 py-1.5">Baris</th><th className="text-left px-2 py-1.5">Nama</th><th className="text-left px-2 py-1.5">Keterangan</th></tr></thead>
                  <tbody>{issues.map((x) => (
                    <tr key={`${x.t}-${x.row}`} className="border-t"><td className="px-2 py-1.5 font-mono">{x.row}</td><td className="px-2 py-1.5">{x.name || "-"}</td>
                      <td className={`px-2 py-1.5 ${x.t === "Gagal" ? "text-rose-700" : "text-amber-700"}`}>{x.t}: {x.reason}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function CustomerForm({ open, initial, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF(initial ? { name: initial.name, phone: initial.phone || "", address: initial.address || "", note: initial.note || "" } : EMPTY); }, [open, initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (initial) await api.put(`/customers/${initial.id}`, f); else await api.post("/customers", f);
      toast.success(initial ? "Customer diperbarui." : "Customer ditambahkan.");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="customer-form-dialog">
        <DialogHeader><DialogTitle>{initial ? "Ubah Customer" : "Tambah Customer"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nama Customer <span className="text-red-500">*</span></Label><Input value={f.name} onChange={set("name")} required className="mt-1.5" data-testid="customer-name" /></div>
          <div><Label>No. HP / WA</Label><Input type="tel" value={f.phone} onChange={set("phone")} className="mt-1.5" data-testid="customer-phone" /></div>
          <div><Label>Alamat</Label><Input value={f.address} onChange={set("address")} className="mt-1.5" data-testid="customer-address" /></div>
          <div><Label>Catatan</Label><Textarea value={f.note} onChange={set("note")} rows={2} className="mt-1.5" data-testid="customer-note" /></div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="customer-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CustomerDetail({ id, onClose }) {
  const [c, setC] = useState(null);
  useEffect(() => { setC(null); if (id) api.get(`/customers/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="customer-detail-dialog">
        {!c ? <div className="h-4 rounded-md skeleton-shimmer" /> : (
          <>
            <DialogHeader>
              <DialogTitle>{c.name}</DialogTitle>
              <DialogDescription>{c.phone || "-"} · {c.address || "-"}{c.note ? ` · ${c.note}` : ""}</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg bg-secondary p-3"><p className="text-xs text-muted-foreground">Total Transaksi ({c.transactions.length})</p><p className="font-mono font-bold" data-testid="customer-detail-total">{formatRupiah(c.total)}</p></div>
              <div className="rounded-lg bg-rose-50 border border-rose-200 p-3"><p className="text-xs text-rose-800">Sisa Piutang</p><p className="font-mono font-bold text-rose-700" data-testid="customer-detail-remaining">{formatRupiah(c.remaining)}</p></div>
            </div>
            {c.transactions.length === 0 ? <p className="text-sm text-muted-foreground text-center py-4">Belum ada transaksi.</p> : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-xs" data-testid="customer-tx-table">
                  <thead className="bg-secondary text-muted-foreground"><tr>
                    <th className="text-left px-3 py-2">Tanggal</th><th className="text-left px-3 py-2">Nota</th><th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Jumlah</th><th className="text-right px-3 py-2">Sisa</th></tr></thead>
                  <tbody>
                    {c.transactions.map((t) => (
                      <tr key={t.id} className="border-t">
                        <td className="px-3 py-2 whitespace-nowrap">{formatDate(t.date)}</td>
                        <td className="px-3 py-2"><Link to={`/transactions/${t.id}`} className="text-brand hover:underline font-mono" data-testid={`customer-tx-link-${t.id}`}>{t.nota_number}</Link></td>
                        <td className="px-3 py-2">{PAY_STATUS_LABEL[t.pay_status]}{t.pay_status === "kredit" && <span className="ml-1"><PayStateBadge state={t.payment_state} /></span>}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatRupiah(t.amount)}</td>
                        <td className="px-3 py-2 text-right font-mono">{formatRupiah(t.remaining)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ open: false, initial: null });
  const [detail, setDetail] = useState(null);
  const load = useCallback(() => { api.get("/customers").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  useEffect(() => { load(); }, [load]);
  const s = q.trim().toLowerCase();
  const list = (rows || []).filter((c) => !s || [c.name, c.phone, c.address].some((x) => (x || "").toLowerCase().includes(s)));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Contact className="h-6 w-6 text-brand" /> Data Customer</h2>
          <p className="text-sm text-muted-foreground">Daftar pelanggan Anda. Customer baru dari Input Transaksi otomatis tersimpan di sini.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" onClick={() => saveBlob("/customers/template", "template_import_customer.xlsx")} className="text-brand" data-testid="customer-template-btn"><FileSpreadsheet className="h-4 w-4 mr-1" /> Template</Button>
          <ImportButton onDone={load} />
          <Button variant="outline" onClick={() => saveBlob("/customers/export", "data_customer.xlsx")} className="border-brand text-brand" data-testid="customer-export-btn"><Download className="h-4 w-4 mr-1" /> Export</Button>
          <Button onClick={() => setForm({ open: true, initial: null })} className="bg-brand hover:bg-emerald-800" data-testid="customer-add-btn"><Plus className="h-4 w-4 mr-1" /> Tambah Customer</Button>
        </div>
      </div>
      <Card className="p-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
          <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Customer ({list.length})</h3>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama / HP / alamat" className="max-w-xs h-9" data-testid="customer-search" />
        </div>
        {!rows ? <div className="p-6"><div className="h-4 rounded-md skeleton-shimmer" /></div> : list.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground" data-testid="customer-empty">Belum ada customer.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customer-table">
              <thead className="bg-secondary text-xs uppercase text-muted-foreground"><tr>
                <th className="text-left px-4 py-2.5">Nama</th><th className="text-left px-4 py-2.5">No. HP</th><th className="text-left px-4 py-2.5">Alamat</th>
                <th className="text-right px-4 py-2.5">Transaksi</th><th className="text-right px-4 py-2.5">Sisa Piutang</th><th className="text-right px-4 py-2.5">Terakhir</th><th className="px-4 py-2.5"></th></tr></thead>
              <tbody>
                {list.map((c) => (
                  <tr key={c.id} onClick={() => setDetail(c.id)} className="border-t cursor-pointer hover:bg-secondary/60" data-testid={`customer-row-${c.id}`}>
                    <td className="px-4 py-2.5 font-medium">{c.name}{c.note && <span className="block text-xs text-muted-foreground">{c.note}</span>}</td>
                    <td className="px-4 py-2.5 font-mono text-xs">{c.phone || "-"}</td>
                    <td className="px-4 py-2.5 text-xs">{c.address || "-"}</td>
                    <td className="px-4 py-2.5 text-right"><span className="font-mono">{formatRupiah(c.total)}</span><span className="block text-xs text-muted-foreground">{c.tx_count} nota</span></td>
                    <td className={`px-4 py-2.5 text-right font-mono ${c.remaining > 0 ? "text-rose-600 font-semibold" : ""}`} data-testid={`customer-remaining-${c.id}`}>{formatRupiah(c.remaining)}</td>
                    <td className="px-4 py-2.5 text-right text-xs whitespace-nowrap">{c.last_tx_date ? formatDate(c.last_tx_date) : "-"}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {waLink(c.phone) && <a href={waLink(c.phone)} target="_blank" rel="noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-md text-emerald-700 hover:bg-emerald-50" data-testid={`customer-wa-${c.id}`}><MessageCircle className="h-4 w-4" /></a>}
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setForm({ open: true, initial: c })} data-testid={`customer-edit-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <CustomerForm open={form.open} initial={form.initial} onClose={() => setForm({ open: false, initial: null })} onSaved={load} />
      <CustomerDetail id={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
